import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { LegalLinks } from "@/components/LegalLinks";
import { SectionLabel } from "@/components/Status";
import { useAuth } from "@/context/AuthContext";
import { LOCALE, useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

export default function Account() {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(LOCALE[lang]) : "—");

  const exportData = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/auth/me/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: `export_${user.id}.json` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("account.exported"));
    } catch (e) { toast.error(t(`errors.${errCode(e)}`, errCode(e))); } finally { setBusy(""); }
  };

  const remove = async () => {
    if (!window.confirm(t("account.deleteConfirm"))) return;
    setBusy("delete");
    try {
      await api.delete("/auth/me");
      toast.success(t("account.deleted"));
      await logout();
      navigate("/");
    } catch (e) { toast.error(t(`errors.${errCode(e)}`, errCode(e))); setBusy(""); }
  };

  const rows = [
    [t("auth.name"), user.name], [t("auth.email"), user.email], [t("account.provider"), user.auth_provider],
    [t("org.title"), user.org_name || "—"], [t("account.consentAt"), fmt(user.consent_at)], [t("account.policyVersion"), user.consent_version || "—"],
  ];

  return (
    <div className="min-h-screen" data-testid="account-page">
      <Nav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="reveal mb-12">
          <div className="mono-label mb-3">{t("account.eyebrow")}</div>
          <h1 className="text-4xl font-semibold tracking-tight text-vox-text sm:text-5xl">{t("account.title")}</h1>
        </div>
        <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
          <section className="reveal reveal-1 min-w-0">
            <SectionLabel index="01">{t("account.personal")}</SectionLabel>
            <dl className="divide-y divide-vox-line rounded-2xl border border-vox-line" data-testid="account-personal-data">
              {rows.map(([k, v]) => (
                <div key={k} className="grid gap-1 px-5 py-3 sm:grid-cols-[220px_1fr]">
                  <dt className="font-mono text-[11px] uppercase tracking-wider text-vox-dim">{k}</dt>
                  <dd className="text-sm text-vox-text">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-vox-dim"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-vox-accent" />{t("account.rightsNote")} <LegalLinks /></p>
          </section>
          <aside className="reveal reveal-2 space-y-6">
            <SectionLabel index="02">{t("account.actions")}</SectionLabel>
            <div className="panel p-6">
              <div className="mb-2 text-sm font-medium text-vox-text">{t("account.export")}</div>
              <p className="mb-4 text-xs leading-relaxed text-vox-muted">{t("account.exportHint")}</p>
              <button type="button" onClick={exportData} disabled={!!busy} className="btn-ghost w-full" data-testid="account-export-button">
                {busy === "export" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {t("account.exportBtn")}
              </button>
            </div>
            <div className="panel border-vox-rec/30 p-6">
              <div className="mb-2 text-sm font-medium text-[#ff9b9e]">{t("account.delete")}</div>
              <p className="mb-4 text-xs leading-relaxed text-vox-muted">{t("account.deleteHint")}</p>
              <button type="button" onClick={remove} disabled={!!busy || user.role === "platform_admin"} className="btn-ghost w-full !border-vox-rec/40 !text-[#ff9b9e] hover:!bg-vox-rec/10" data-testid="account-delete-button">
                {busy === "delete" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} {t("account.deleteBtn")}
              </button>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
